import { useState } from 'react';
import { api, enc } from '../api';
import { fmtWhen } from '../format';
import { useFeedback } from './feedback';
import { fmtBytes, upload, type Att } from './attach';
import { FileDrop } from './dropzone';

// The assets dropped on a campaign: anything already made toward its goal. With a `mid`, a file links to the
// campaign the moment it lands and the agents working the campaign read it. Without one (a campaign not yet saved),
// the files are held in the parent's state and linked on save.
export type CampaignFile = Att & { note?: string | null; uploaded_by_name?: string | null; created_at?: string; entity_type?: string; can_edit?: boolean };
type Up = { local: string; name: string; size: number; busy: boolean; error?: string };
const KIND_LABEL: Record<string, string> = { image: 'Image', pdf: 'PDF', text: 'Text', doc: 'Word', sheet: 'Excel', slides: 'PowerPoint' };

export function CampaignFiles({ mid, files, canEdit, onChange, onPending }: { mid?: string; files: CampaignFile[]; canEdit: boolean; onChange?: () => void; onPending?: (add: Att[] | null, removeId?: string) => void }) {
  const { toast } = useFeedback();
  const [ups, setUps] = useState<Up[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  let seq = 0;
  const add = async (list: File[]) => {
    const started = list.map((f) => ({ local: `u${Date.now()}-${++seq}`, name: f.name, size: f.size, busy: true }));
    setUps((xs) => [...xs, ...started]);
    const landed: Att[] = [];
    await Promise.all(list.map(async (f, i) => {
      const u = started[i];
      try {
        const att = await upload(f);
        if (mid) await api('POST', `/campaigns/${enc(mid)}/files`, { attachments: [att.id] });
        else landed.push(att);
        setUps((xs) => xs.filter((x) => x.local !== u.local));
      } catch (e) { setUps((xs) => xs.map((x) => (x.local === u.local ? { ...x, busy: false, error: (e as Error).message } : x))); }
    }));
    if (mid) onChange?.(); else if (landed.length) onPending?.(landed);
  };
  const saveNote = async (f: CampaignFile, note: string) => {
    if (!mid || (f.note ?? '') === note.trim()) return;
    try { await api('PATCH', `/campaigns/${enc(mid)}/files/${f.id}`, { note: note.trim() }); onChange?.(); } catch (e) { toast((e as Error).message, true); }
  };
  const remove = async (f: CampaignFile) => {
    if (!mid) { onPending?.(null, f.id); return; }
    if (!confirm(`Remove "${f.filename}" from this campaign? The file is deleted.`)) return;
    setBusyId(f.id);
    try { await api('DELETE', `/campaigns/${enc(mid)}/files/${f.id}`); toast('Removed'); onChange?.(); } catch (e) { toast((e as Error).message, true); } finally { setBusyId(null); }
  };
  return (
    <div className="campaign-files">
      {canEdit && <FileDrop onFiles={add} label="Drop the assets already made toward this campaign's goal" hint="Decks, one-pagers, briefs, copy, screenshots: images, PDF, Word, Excel, PowerPoint or text, up to 10 MB each. The agents working this campaign read them." />}
      {ups.length > 0 && <div className="att-chips pending">{ups.map((u) => <span key={u.local} className={`att ${u.error ? 'bad' : 'busy'}`} title={u.error ?? u.name}><i className="k">{(u.name.split('.').pop() ?? '').toUpperCase()}</i><span className="n">{u.name}</span><small>{u.error ? u.error : 'uploading…'}</small>{u.error && <button type="button" aria-label="Dismiss" onClick={() => setUps((xs) => xs.filter((x) => x.local !== u.local))}>×</button>}</span>)}</div>}
      {files.length > 0 ? (
        <div className="file-rows">
          {files.map((f) => (
            <div key={f.id} className="file-row">
              <a className="file-ico" href={f.url} target="_blank" rel="noreferrer" title={f.filename}>{f.kind === 'image' ? <img src={f.url} alt="" loading="lazy" /> : <i className="k">{KIND_LABEL[f.kind] ?? f.kind}</i>}</a>
              <div className="file-main">
                <a href={f.url} target="_blank" rel="noreferrer" className="file-name">{f.filename}</a>
                <div className="small muted">{fmtBytes(f.size_bytes)}{f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''}{f.created_at ? ` · ${fmtWhen(f.created_at)}` : ''}{f.entity_type === 'track' ? ' · on a track' : ''}</div>
                {(f.can_edit ?? canEdit) && mid && f.entity_type !== 'track' ? <input className="file-note" defaultValue={f.note ?? ''} placeholder="What is it? One line the agents will read, e.g. the approved one-pager" maxLength={300} onBlur={(e) => saveNote(f, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                  : f.note ? <div className="small">{f.note}</div> : null}
              </div>
              {(f.can_edit ?? canEdit) && f.entity_type !== 'track' && <button type="button" className="quiet sm" disabled={busyId === f.id} onClick={() => remove(f)}>Remove</button>}
            </div>
          ))}
        </div>
      ) : !canEdit ? <div className="muted small">No assets dropped yet.</div> : null}
    </div>
  );
}
